/**
 * Merges two objects, prioritizing existing values over undefined/null values.
 *
 * @param baseObject - The base object (usually from localStorage or cache)
 * @param updateObject - The object with updates (usually from server response)
 * @returns A new object with merged data, preserving existing values when possible
 *
 * @example
 * const cached = { name: "John", age: null, city: "NYC" };
 * const server = { name: "John Doe", age: 30, city: undefined };
 * const result = mergeObjectsPreservingValues(cached, server);
 * // Result: { name: "John Doe", age: 30, city: "NYC" }
 */
export const mergeObjectsPreservingValues = <T extends Record<string, any>>(
  baseObject: T,
  updateObject: Partial<T>,
): T => {
  const merged: any = { ...baseObject };

  Object.keys(updateObject).forEach((key) => {
    const baseValue = baseObject[key];
    const updateValue = updateObject[key];

    if (
      updateValue !== undefined &&
      updateValue !== null &&
      (baseValue === undefined || baseValue === null)
    ) {
      merged[key] = updateValue;
    } else if (
      updateValue !== undefined &&
      updateValue !== null &&
      baseValue !== undefined &&
      baseValue !== null
    ) {
      merged[key] = updateValue;
    }
  });

  return merged as T;
};
